import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Plus, PencilSimple, Trash, ArrowUp, ArrowDown, ArrowLeft, CaretDown, CaretRight, Translate, ArrowsOut, ArrowsIn } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { getTranslation } from '../i18n/translations';
import AgendaTemplateEditor, { resolveAgendaVars, AGENDA_SAMPLE_VARS } from '../components/AgendaTemplateEditor';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// -----------------------------------------------------------------------------
// Phase 735 — Agenda Templates Editor UX overhaul.
//
// SCOPE: This file is the ONLY file affected by the redesign. The Meetings
// module, Meeting Settings, General Meeting Templates and PDF generation are
// intentionally NOT touched.
//
// Key changes vs. the previous version:
//   • Larger modal (95vw / 90vh) that supports Full Screen mode.
//   • Rich-text editor (react-quill-new) per topic — Bold, Italic, Underline,
//     bullet + numbered lists, alignment, undo / redo.
//   • "Insert Variable ▼" and "Insert Snippet ▼" dropdowns above each editor.
//   • Live Preview tab that renders the final text with variables resolved
//     using representative sample data.
//   • Data model: we now persist BOTH `greek_template_html` (rich HTML) AND
//     `greek_template_text` (plain-text mirror extracted via Quill's
//     `getText()`). The MeetingsTab pipeline continues to read the plain
//     text mirror verbatim, so no downstream change is required.
// -----------------------------------------------------------------------------
export default function AgendaTemplates() {
  useLanguage();
  const t = (key) => getTranslation('el', key);
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [form, setForm] = useState({ name: '', topics: [] });
  // Full Screen toggle. When true, the modal fills the viewport so users
  // can compose long minutes text comfortably.
  const [fullScreen, setFullScreen] = useState(false);

  // Refs to each topic's editor — used at save time to pull the plain-text
  // mirror out of Quill.
  const editorRefs = useRef({}); // { [idx]: { greek: ref, english: ref } }
  const getEditorRef = (idx, lang) => {
    if (!editorRefs.current[idx]) editorRefs.current[idx] = { greek: React.createRef(), english: React.createRef() };
    return editorRefs.current[idx][lang];
  };

  const fetchData = async () => {
    try {
      const tRes = await axios.get(`${API}/agenda-templates`, { withCredentials: true });
      setTemplates(tRes.data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { fetchData(); }, []);

  const blankTopic = () => ({
    title: '',
    greek_template_text: '',
    greek_template_html: '',
    english_template_text: '',
    english_template_html: '',
  });

  const openAdd = () => {
    setEditing(null);
    setForm({ name: '', topics: [blankTopic()] });
    editorRefs.current = {};
    setFullScreen(false);
    setDialogOpen(true);
  };

  const openEdit = (tmpl) => {
    setEditing(tmpl);
    editorRefs.current = {};
    setForm({
      name: tmpl.name_el || tmpl.name || '',
      topics: tmpl.topics?.length
        ? tmpl.topics.map((tp) => ({
            title: tp.title_el || tp.title || '',
            greek_template_text: tp.greek_template_text || '',
            // Fall back to plain text when older templates lack the HTML mirror.
            greek_template_html:
              tp.greek_template_html || textToHtml(tp.greek_template_text || ''),
            english_template_text: tp.english_template_text || '',
            english_template_html:
              tp.english_template_html || textToHtml(tp.english_template_text || ''),
          }))
        : [blankTopic()],
    });
    setFullScreen(false);
    setDialogOpen(true);
  };

  const addTopic = () => setForm((prev) => ({ ...prev, topics: [...prev.topics, blankTopic()] }));

  const updateTopic = (idx, field, val) => {
    setForm((prev) => {
      const topics = [...prev.topics];
      topics[idx] = { ...topics[idx], [field]: val };
      return { ...prev, topics };
    });
  };

  const removeTopic = (idx) => {
    delete editorRefs.current[idx];
    setForm((prev) => ({ ...prev, topics: prev.topics.filter((_, i) => i !== idx) }));
  };

  const moveTopic = (idx, dir) => {
    setForm((prev) => {
      const topics = [...prev.topics];
      const newIdx = idx + dir;
      if (newIdx < 0 || newIdx >= topics.length) return prev;
      [topics[idx], topics[newIdx]] = [topics[newIdx], topics[idx]];
      return { ...prev, topics };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const greekName = form.name.trim();
    if (!greekName) return;

    // Snapshot the plain-text mirror out of each Quill editor so the existing
    // MeetingsTab pipeline (which still reads plain text) keeps working.
    const topics = form.topics.map((tp, idx) => {
      const greekPlain = editorRefs.current[idx]?.greek?.current?.getPlainText?.() ?? tp.greek_template_text ?? '';
      const englishPlain = editorRefs.current[idx]?.english?.current?.getPlainText?.() ?? tp.english_template_text ?? '';
      return {
        title: tp.title,
        title_el: tp.title,
        category_id: '',
        greek_template_text: greekPlain,
        greek_template_html: tp.greek_template_html || '',
        english_template_text: englishPlain,
        english_template_html: tp.english_template_html || '',
        // Kept for backward compatibility with older consumers of `decision`.
        decision: greekPlain,
      };
    });

    const payload = { name: greekName, name_el: greekName, topics };
    try {
      if (editing) {
        await axios.put(`${API}/agenda-templates/${editing.template_id}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API}/agenda-templates`, payload, { withCredentials: true });
      }
      toast.success(t('savedSuccess'));
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/agenda-templates/${deleting.template_id}`, { withCredentials: true });
      toast.success(t('deletedSuccess'));
      setDeleteOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    }
  };

  // ---------------------------------------------------------------------------
  // Modal sizing. `fullScreen` fills the viewport; otherwise a resizable
  // container lets the user drag the bottom-right corner to grow the dialog.
  // ---------------------------------------------------------------------------
  const dialogContentClass = fullScreen
    ? 'w-screen h-screen max-w-none max-h-none rounded-none p-0 flex flex-col'
    : 'w-[95vw] max-w-[1400px] h-[90vh] max-h-[90vh] rounded-none p-0 flex flex-col sm:max-w-[1400px]';

  return (
    <Layout>
      <div data-testid="agenda-templates-page">
        <button
          onClick={() => navigate('/building-settings')}
          className="flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-900 mb-4"
          data-testid="agenda-templates-back-to-settings"
        >
          <ArrowLeft size={14} />
          <span className="font-semibold">{t('settings') || 'Ρυθμίσεις'}</span>
        </button>
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-xl font-bold" style={{ fontFamily: 'var(--font-heading)' }}>{t('agendaTemplates')}</h1>
          {isAdmin && (
            <Button onClick={openAdd} className="bg-zinc-950 rounded-none" data-testid="add-template-btn">
              <Plus size={18} className="mr-2" />{t('addTemplate')}
            </Button>
          )}
        </div>

        {templates.length === 0 ? (
          <div className="bg-white border border-zinc-200 p-12 text-center text-zinc-500" data-testid="no-templates">{t('noTemplates')}</div>
        ) : (
          <div className="bg-white border border-zinc-200 overflow-hidden">
            <table className="w-full text-sm" data-testid="templates-table">
              <thead>
                <tr className="bg-zinc-50 border-b border-zinc-200">
                  <th className="text-left px-5 py-3 text-xs font-semibold text-zinc-500 uppercase tracking-wider">{t('templateName')}</th>
                  <th className="text-center px-5 py-3 text-xs font-semibold text-zinc-500 uppercase tracking-wider">{t('topicCount')}</th>
                  {isAdmin && <th className="text-right px-5 py-3 text-xs font-semibold text-zinc-500 uppercase tracking-wider w-24">{t('actions')}</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {templates.map((tmpl) => (
                  <tr key={tmpl.template_id} className="hover:bg-zinc-50 transition-colors cursor-pointer" onClick={() => openEdit(tmpl)} data-testid={`template-row-${tmpl.template_id}`}>
                    <td className="px-5 py-3 font-semibold">{tmpl.name_el || tmpl.name}</td>
                    <td className="px-5 py-3 text-center font-mono">{(tmpl.topics || []).length}</td>
                    {isAdmin && (
                      <td className="px-5 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-end gap-1">
                          <button onClick={() => openEdit(tmpl)} className="p-1.5 hover:bg-zinc-100 transition-colors" data-testid={`edit-tmpl-${tmpl.template_id}`}><PencilSimple size={16} /></button>
                          <button onClick={() => { setDeleting(tmpl); setDeleteOpen(true); }} className="p-1.5 hover:bg-red-50 text-red-600 transition-colors" data-testid={`delete-tmpl-${tmpl.template_id}`}><Trash size={16} /></button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className={dialogContentClass} data-testid="agenda-tmpl-dialog">
            <DialogHeader className="px-6 pt-5 pb-3 border-b border-zinc-200 flex-shrink-0">
              <div className="flex items-center justify-between gap-3">
                <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>
                  {editing ? t('editTemplate') : t('addTemplate')}
                </DialogTitle>
                <button
                  type="button"
                  onClick={() => setFullScreen((v) => !v)}
                  className="flex items-center gap-1.5 text-xs font-semibold text-zinc-600 hover:text-zinc-900 border border-zinc-300 px-2.5 py-1.5 hover:bg-zinc-50 transition-colors"
                  data-testid="agenda-tmpl-fullscreen-toggle"
                  title={fullScreen ? 'Έξοδος από πλήρη οθόνη' : 'Πλήρης οθόνη'}
                >
                  {fullScreen ? <ArrowsIn size={14} /> : <ArrowsOut size={14} />}
                  <span>{fullScreen ? 'Έξοδος' : 'Full Screen'}</span>
                </button>
              </div>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="flex-1 flex flex-col overflow-hidden">
              <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
                <div>
                  <Label>{t('templateName')} *</Label>
                  <Input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="mt-1 rounded-none"
                    required
                    data-testid="tmpl-name"
                    placeholder={t('templateName')}
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-3">
                    <Label className="text-sm font-semibold">{t('topics')}</Label>
                    <Button type="button" onClick={addTopic} size="sm" variant="outline" className="rounded-none" data-testid="tmpl-add-topic">
                      <Plus size={14} className="mr-1" />{t('addTopic')}
                    </Button>
                  </div>
                  <div className="space-y-4">
                    {form.topics.map((topic, idx) => (
                      <TopicRow
                        key={idx}
                        idx={idx}
                        topic={topic}
                        total={form.topics.length}
                        t={t}
                        onChange={(field, val) => updateTopic(idx, field, val)}
                        onRemove={() => removeTopic(idx)}
                        onMove={(dir) => moveTopic(idx, dir)}
                        greekEditorRef={getEditorRef(idx, 'greek')}
                        englishEditorRef={getEditorRef(idx, 'english')}
                      />
                    ))}
                  </div>
                </div>
              </div>

              <DialogFooter className="px-6 py-4 border-t border-zinc-200 flex-shrink-0 bg-zinc-50/50">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-none">{t('cancel')}</Button>
                <Button type="submit" className="bg-zinc-950 rounded-none" data-testid="save-template-btn">{t('save')}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
          <AlertDialogContent>
            <AlertDialogHeader><AlertDialogTitle>{t('deleteTemplate')}</AlertDialogTitle><AlertDialogDescription>{t('confirmDeleteTemplate')}</AlertDialogDescription></AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-none">{t('cancel')}</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-red-600 rounded-none" data-testid="confirm-delete-tmpl">{t('delete')}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}

// ---------------------------------------------------------------------------
// Individual topic editor.
// ---------------------------------------------------------------------------
function TopicRow({ idx, topic, total, t, onChange, onRemove, onMove, greekEditorRef, englishEditorRef }) {
  const [enExpanded, setEnExpanded] = useState(false);
  // Preview toggles per-language so the user can preview Greek and English
  // independently without losing their edit position.
  const [greekTab, setGreekTab] = useState('edit'); // 'edit' | 'preview'
  const [englishTab, setEnglishTab] = useState('edit');
  const hasEnglish = (topic.english_template_text || '').trim().length > 0
    || (topic.english_template_html || '').trim().length > 0;

  return (
    <div className="border border-zinc-200 p-4 bg-zinc-50/40 space-y-3" data-testid={`tmpl-topic-${idx}`}>
      <div className="flex items-start gap-2">
        <span className="text-xs font-bold text-zinc-400 mt-2.5 w-5 flex-shrink-0">{idx + 1}.</span>
        <Input
          value={topic.title}
          onChange={(e) => onChange('title', e.target.value)}
          className="rounded-none text-sm flex-1"
          placeholder={t('topicPlaceholder')}
          data-testid={`tmpl-topic-title-${idx}`}
        />
        <div className="flex flex-col gap-0.5 flex-shrink-0">
          <button type="button" onClick={() => onMove(-1)} disabled={idx === 0} className="p-1 hover:bg-zinc-200 disabled:opacity-30 transition-colors"><ArrowUp size={14} /></button>
          <button type="button" onClick={() => onMove(1)} disabled={idx === total - 1} className="p-1 hover:bg-zinc-200 disabled:opacity-30 transition-colors"><ArrowDown size={14} /></button>
          {total > 1 && <button type="button" onClick={onRemove} className="p-1 hover:bg-red-50 text-red-500"><Trash size={14} /></button>}
        </div>
      </div>

      {/* Greek editor + preview */}
      <div className="pl-7">
        <div className="flex items-center justify-between mb-1">
          <Label className="text-[11px] text-zinc-600 block">
            {t('greekTemplateText') || 'Ελληνικό Κείμενο Προτύπου'}
          </Label>
          <TabToggle current={greekTab} onChange={setGreekTab} testId={`tmpl-topic-greek-tabs-${idx}`} />
        </div>
        {greekTab === 'edit' ? (
          <AgendaTemplateEditor
            ref={greekEditorRef}
            value={topic.greek_template_html || ''}
            onChange={(html) => onChange('greek_template_html', html)}
            placeholder={t('greekTemplatePlaceholder') || 'Προεπιλεγμένο κείμενο που θα εισάγεται στη συνέλευση όταν επιλέγεται αυτό το θέμα.'}
            minHeight="220px"
            testId={`tmpl-topic-greek-editor-${idx}`}
          />
        ) : (
          <PreviewPane html={topic.greek_template_html || ''} testId={`tmpl-topic-greek-preview-${idx}`} />
        )}
      </div>

      {/* English translation — collapsible */}
      <div className="pl-7">
        <button
          type="button"
          onClick={() => setEnExpanded((v) => !v)}
          className="flex items-center gap-1 text-[11px] text-zinc-500 hover:text-zinc-900 font-semibold border-t border-zinc-200 pt-2 w-full"
          data-testid={`tmpl-topic-en-toggle-${idx}`}
        >
          {enExpanded ? <CaretDown size={12} /> : <CaretRight size={12} />}
          <Translate size={12} />
          <span>{t('englishTranslation') || 'English Translation'}</span>
          {hasEnglish && (
            <span className="ml-1 inline-block text-[9px] px-1 border border-emerald-300 bg-emerald-50 text-emerald-700 uppercase">
              {t('filled') || 'Filled'}
            </span>
          )}
        </button>
        {enExpanded && (
          <div className="mt-2">
            <div className="flex items-center justify-end mb-1">
              <TabToggle current={englishTab} onChange={setEnglishTab} testId={`tmpl-topic-english-tabs-${idx}`} />
            </div>
            {englishTab === 'edit' ? (
              <AgendaTemplateEditor
                ref={englishEditorRef}
                value={topic.english_template_html || ''}
                onChange={(html) => onChange('english_template_html', html)}
                placeholder={t('englishTemplatePlaceholder') || 'Optional English version of the template text.'}
                minHeight="200px"
                testId={`tmpl-topic-english-editor-${idx}`}
              />
            ) : (
              <PreviewPane html={topic.english_template_html || ''} testId={`tmpl-topic-english-preview-${idx}`} />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Small helper components
// ---------------------------------------------------------------------------
function TabToggle({ current, onChange, testId }) {
  const btn = (mode, label) => (
    <button
      type="button"
      onClick={() => onChange(mode)}
      className={`text-[11px] px-2 py-0.5 border ${current === mode ? 'bg-zinc-950 text-white border-zinc-950' : 'bg-white text-zinc-600 border-zinc-300 hover:bg-zinc-100'}`}
      data-testid={`${testId}-${mode}`}
    >
      {label}
    </button>
  );
  return (
    <div className="flex gap-0" data-testid={testId}>
      {btn('edit', 'Edit')}
      {btn('preview', 'Preview')}
    </div>
  );
}

function PreviewPane({ html, testId }) {
  const resolved = resolveAgendaVars(html || '', AGENDA_SAMPLE_VARS);
  return (
    <div
      className="border border-zinc-200 bg-white px-4 py-3 text-sm min-h-[220px] agenda-tmpl-preview"
      data-testid={testId}
    >
      <div className="text-[10px] uppercase tracking-wider text-zinc-400 mb-2">
        Preview · sample values shown for variables
      </div>
      {html ? (
        <div className="prose prose-sm max-w-none" style={{ fontFamily: 'Georgia, serif' }} dangerouslySetInnerHTML={{ __html: resolved }} />
      ) : (
        <div className="text-zinc-400 italic">— Empty template —</div>
      )}
      <style>{`
        .agenda-tmpl-preview p { margin: 0 0 6px 0; }
        .agenda-tmpl-preview ul, .agenda-tmpl-preview ol { margin: 4px 0 8px 20px; }
      `}</style>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Best-effort plain-text → HTML upgrade so existing (pre-Phase-735) templates
// display correctly the first time they're opened in the new editor. Each
// non-empty line becomes a <p>. Blank lines produce an empty paragraph.
// -----------------------------------------------------------------------------
function textToHtml(text) {
  if (!text) return '';
  const escaped = String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  const lines = escaped.split(/\r?\n/);
  return lines.map((l) => `<p>${l || '<br/>'}</p>`).join('');
}
